import { DestroyRef, Directive, HostBinding, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { AppSetupApi } from 'api/app-setup/app-setup-api.js';
import { AppDataService } from 'lib/app-data.service.js';
import { AuthService } from 'lib/auth/auth.service.js';
import { CookieService } from 'lib/cookies/cookie.service.js';
import { AppSetupDb } from 'lib/cosmos/app-setup/appSetupDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { cloneHelper } from 'lib/helpers/clone-helper.class.js';
import { nameHelper } from 'lib/helpers/name-helper.js';
import { observableHelper } from 'lib/helpers/observable-helper.class.js';
import { PlatformService } from 'lib/platform/index.js';
import { PwaService } from 'lib/pwa/index.js';
import { ScreenService, ScreenSize } from 'lib/screen/index.js';
import { DateSource } from 'lib/time/index.js';
import { ITrialState, TrialService } from 'lib/trial/trial.service.js';
import { StringUnitLength } from 'luxon';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ItemType } from 'src/app/core/core.models.js';
import { HttpService } from 'src/app/http/http.service.js';
import { AppNotificationService } from 'src/app/services/app-notification.service.js';
import { AppVersionService, IAppVersionState } from 'src/app/services/app-version.service.js';
import { TimeService } from 'src/app/services/time.service.js';
import { IButton } from '../buttons/button.models.js';
import { IconType } from '../icons/index.js';
import { AppTheme } from '../theme/index.js';
import { ThemeService } from '../theme/theme.service.js';

@Directive()
export abstract class CoreComponent {
	@HostBinding('style') style = 'display: block';

	private readonly destroyRef = inject(DestroyRef);

	protected readonly httpService: HttpService = inject(HttpService);
	protected readonly timeService: TimeService = inject(TimeService);
	protected readonly screenService: ScreenService = inject(ScreenService);
	protected readonly authService: AuthService = inject(AuthService);
	protected readonly platformService: PlatformService = inject(PlatformService);
	protected readonly themeService: ThemeService = inject(ThemeService);
	protected readonly appDataService: AppDataService = inject(AppDataService);
	protected readonly appVersionService: AppVersionService = inject(AppVersionService);
	protected readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
	protected readonly pwaService: PwaService = inject(PwaService);
	protected readonly trialService: TrialService = inject(TrialService);
	protected readonly notificationService: AppNotificationService = inject(AppNotificationService);
	protected readonly cookieService: CookieService = inject(CookieService);

	protected readonly roundImages = signal<boolean>(true);
	protected readonly screenSize = computed<ScreenSize>(() => this.screenService.currentScreen().screenSize);
	protected readonly isMobileView = computed<boolean>(() => this.screenService.currentScreen().isMobileView);

	protected readonly showInstallOnDashboard = computed<boolean>(() => {
		if (this.isServerPlatform()) {
			// do not render / show install button on server platform as it's never supported
			// and would cause a flicker when the page is loaded e.g. within the PWA app
			return false;
		}
		return this.pwaService.isInstallSupportedOnCurrentDevice() && !this.pwaService.isInstalled();
	});

	protected readonly pwaInstallState = computed<'installed' | 'notSupported' | 'installable'>(() => {
		if (this.pwaService.isInstalled()) {
			return 'installed';
		}
		if (this.pwaService.isInstallSupportedOnCurrentDevice()) {
			return 'installable';
		}
		return 'notSupported';
	});

	protected readonly isDesktopExtraView = computed<boolean>(
		() => this.screenService.currentScreen().isDesktopExtraView
	);
	protected readonly isDesktopView = computed<boolean>(() => this.screenService.currentScreen().isDesktopView);
	protected readonly isMobileOrTabletView = computed<boolean>(() => this.isTabletView() || this.isMobileView());
	protected readonly isTabletView = computed<boolean>(() => this.screenService.currentScreen().isTabletView);
	protected readonly theme = computed<AppTheme>(() => this.themeService.theme());
	protected readonly isBrowserPlatform = computed<boolean>(() => this.platformService.isBrowser);
	protected readonly isServerPlatform = computed<boolean>(() => this.platformService.isServer);

	protected readonly authUser = computed<UserDb.IAuthUser | undefined>(() => {
		return this.authService.authUser();
	});

	protected readonly trialState = computed<ITrialState>(() => {
		return this.trialService.getTrialState(this.authUser());
	});

	public readonly isTrialExpired = computed<boolean>(() => {
		const trialState = this.trialState();

		if (trialState?.isExpired === true) {
			return true;
		}
		return false;
	});

	protected readonly authUserRequired = computed<UserDb.IAuthUser>(() => {
		const authUser = this.authUser();

		if (!authUser) {
			throw Error(`Auth user is required here`);
		}

		return authUser;
	});

	protected readonly authUserIsTrainer = computed<boolean>(() =>
		this.authUser()?.userType === 'trainer' ? true : false
	);
	protected readonly authUserIsClient = computed<boolean>(() =>
		this.authUser()?.userType === 'client' ? true : false
	);

	protected readonly authUserName = computed<string | undefined>(() => {
		const currentUser = this.authService.authUser();
		if (!currentUser) {
			return undefined;
		}

		const name = nameHelper.getUserName(currentUser.email, currentUser.firstName, currentUser.lastName);

		return name;
	});

	protected readonly appContext = computed<AppSetupApi.IAppContext | undefined>(() => {
		return this.appDataService.appContext();
	});

	protected readonly appSetup = computed<AppSetupDb.IAppSetupItem | undefined>(() => {
		return this.appContext()?.appSetup;
	});

	protected readonly appVersionState = computed<IAppVersionState | undefined>(() => {
		const appSetup = this.appSetup();

		if (!appSetup) {
			return undefined;
		}

		return this.appVersionService.getAppVersionState(appSetup);
	});

	public readonly themeButtonIcon = computed<IconType>(() => {
		if (this.theme() === 'dark') {
			return 'themeDark';
		}
		return 'themeLight';
	});

	public readonly themeButtons = computed<IButton[]>(() => {
		const button: IButton[] = [
			{
				title: 'Light',
				icon: 'themeLight',
				buttonType: this.theme() === 'light' ? 'primary' : 'basic',
				click: () => {
					this.themeService.setTheme('light');
				}
			},
			{
				title: 'Dark',
				icon: 'themeDark',
				buttonType: this.theme() === 'dark' ? 'primary' : 'basic',
				click: () => {
					this.themeService.setTheme('dark');
				}
			}
		];

		return button;
	});

	constructor() {}

	protected fromNow(source: DateSource, style: StringUnitLength = 'long'): string {
		return this.timeService.fromNow(source, style);
	}

	protected fromNowCalendar(source: DateSource): string {
		return this.timeService.fromNowCalendar(source);
	}

	protected formatDate(source: DateSource): string {
		return this.timeService.formatDate(source);
	}

	protected formatDateTime(source: DateSource): string {
		return this.timeService.formatDateTime(source);
	}

	protected getQueryStringValue(name: string): string | undefined {
		return this.activatedRoute.snapshot.queryParams[name];
	}

	protected clone<T>(object: T): T {
		return cloneHelper.clone(object);
	}

	protected calculateFlexWidth(itemsPerRow: number, base: 0 | 24 | 8 | 16 = 24): string {
		return 'calc((100% / ' + itemsPerRow + ') - ' + this.calculateRowGap(itemsPerRow, base) + ')';
	}

	protected subscribeToObservables(observables: Observable<any>[], onAllFinished?: () => void): Subscription {
		return this.subscribeToObservable(
			observableHelper.zipObservables(observables).pipe(
				map(() => {
					if (onAllFinished) {
						onAllFinished();
					}
				})
			)
		);
	}

	protected subscribeToObservable(observable: Observable<any>): Subscription {
		return observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	private calculateRowGap(itemsPerRow: number, base: 0 | 24 | 8 | 16 = 24): string {
		const gapPx = ((itemsPerRow - 1) * base) / itemsPerRow;
		return `${gapPx}px`;
	}

	protected notify(text: string): void {
		this.notificationService.notify(text);
	}

	protected notifyDeleted(itemType: ItemType): void {
		this.notificationService.notifyDeleted(itemType);
	}

	protected notifySaved(itemType: ItemType): void {
		this.notificationService.notifySaved(itemType);
	}

	protected notifyUploaded(): void {
		this.notificationService.notifyUploaded();
	}

	protected notifyCreated(itemType: ItemType): void {
		this.notificationService.notifyCreated(itemType);
	}

	protected notifyUpsert(itemType: ItemType, isEditForm: boolean): void {
		if (isEditForm) {
			this.notificationService.notifySaved(itemType);
		} else {
			this.notificationService.notifyCreated(itemType);
		}
	}
}
